import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, Crown, Sparkles, ChevronLeft, X, Calendar, Clock, MapPin, Users, ExternalLink, Gift, PartyPopper, Ticket, ArrowRight, Globe } from 'lucide-react';
import Logo from '../components/Logo';
import SquarePaymentForm from '../components/SquarePaymentForm';
import AppleIAPButton from '../components/AppleIAPButton';
import { isNativePlatform } from '../lib/platform';

// Countdown component for launch event - Blue/Indigo Theme matching Events page
const PricingPage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null); // '1x' | '4x' | null
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const isNative = isNativePlatform();

  // On native platforms, redirect away from pricing (Apple Guideline 3.1.1)
  useEffect(() => {
    if (isNative) {
      navigate(user ? '/dashboard' : '/', { replace: true });
    }
  }, [navigate, user, isNative]);

  // If native, render nothing while redirecting
  if (isNative) return null;

  const plans = [
    {
      id: 'free',
      name: 'Gratuit',
      priceMonthly: 0,
      priceAnnual: 0,
      period: '',
      description: 'Pour découvrir la plateforme',
      features: [
        'Calculateur de rentabilité basique',
        '1 projet MDB',
        'Accès à la communauté (lecture)',
        'Comparateur fiscal simplifié'
      ],
      notIncluded: [
        'Dossier bancaire PDF',
        'Projets MDB illimités',
        'Messages privés',
        'Biens immobiliers',
        'Formations',
        'Annuaire des membres',
        'Agent IA',
        'Intégrations'
      ],
      cta: 'Commencer gratuitement',
      popular: false
    },
    {
      id: 'club',
      name: 'Club 10M Investor',
      priceTotal: 1997,
      pricePer4x: 499.25,
      duration: '12 mois',
      description: 'Pour les investisseurs actifs et les professionnels',
      features: [
        'Calculateur complet (tous régimes)',
        'Projets MDB illimités',
        'Dossier bancaire PDF',
        'Communauté complète + Badge Club',
        'Messages privés entre membres',
        'Biens immobiliers (publier & consulter)',
        'Formations exclusives',
        'Annuaire des membres',
        'Agent IA (recherche automatique)',
        'Statistiques IA & évolution des prix',
        'Intégrations (Calendly, Zapier)',
        'Alertes email automatiques',
        'Support prioritaire'
      ],
      notIncluded: [],
      cta: 'Rejoindre le Club',
      popular: true
    }
  ];

  const handleSubscribe = async (planId, mode = '1x') => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (planId === 'free') {
      toast.info('Vous êtes déjà sur le plan gratuit');
      return;
    }

    setPaymentMode(mode);
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = async (data) => {
    // Refresh user data to get updated subscription
    if (checkAuth) {
      await checkAuth();
    }
    setShowPaymentForm(false);
    toast.success('Bienvenue dans le Club 10M Investor !');
    navigate('/dashboard?success=true&payment=square');
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setPaymentMode(null);
  };

  // Compute displayed amount based on selected payment mode
  const getDisplayAmount = () => (paymentMode === '4x' ? 499.25 : 1997);
  const getSquarePlanId = () => (paymentMode === '4x' ? 'club_annual_4x' : 'club_annual_1x');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/">
              {/* Logo vertical pour mobile, horizontal pour desktop */}
              <img src="/logo-vertical.png" alt="10 Millions Investor" className="h-14 w-auto md:hidden" />
              <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 w-auto hidden md:block" />
            </Link>
            <div className="flex items-center space-x-4">
              {user ? (
                <Link to="/dashboard">
                  <Button variant="ghost">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Tableau de bord
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost">Connexion</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-[#0D47A1] hover:bg-[#0A367A]">S&apos;inscrire</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Launch Countdown Banner */}

      {/* Pricing Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Choisissez votre plan
          </h1>
          <p className="text-xl font-semibold text-[#0D47A1] mb-2">
            Pour les investisseurs actifs, les marchands de biens et les professionnels
          </p>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Un abonnement unique pour tout débloquer. Engagement 12 mois.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative border-slate-200 transition-all duration-300 ${
                plan.popular ? 'shadow-xl border-[#0D47A1] border-2' : 'hover:shadow-lg hover:border-slate-300'
              }`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-[#0D47A1] to-[#00B050] text-white text-xs font-semibold px-4 py-1 rounded-full">
                    Recommandé
                  </span>
                </div>
              )}
              
              {/* Card Content */}
              
              <CardHeader className="text-center pb-2">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  plan.id === 'free' ? 'bg-slate-100' : 'bg-gradient-to-br from-[#0D47A1]/10 to-[#00B050]/10'
                }`}>
                  {plan.id === 'free' && <Sparkles className="h-7 w-7 text-slate-600" />}
                  {plan.id === 'club' && <Crown className="h-7 w-7 text-[#0D47A1]" />}
                </div>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription className="text-base">{plan.description}</CardDescription>
                <div className="mt-4">
                  {plan.id === 'free' ? (
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold text-slate-900">0€</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-center">
                        <span className="text-5xl font-bold text-slate-900">
                          1 997€
                        </span>
                        <span className="text-slate-500 ml-1 text-lg">TTC</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-2">
                        Accès complet pendant <span className="text-[#00B050] font-semibold">12 mois</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Ou 4 × 499,25€ sans frais · engagement 12 mois
                      </p>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-[#00B050] mr-2 flex-shrink-0" />
                      <span className="text-slate-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.notIncluded.length > 0 && (
                  <ul className="space-y-3 mb-6 pt-3 border-t border-slate-100">
                    {plan.notIncluded.map((feature, index) => (
                      <li key={index} className="flex items-start opacity-50">
                        <X className="h-5 w-5 text-slate-400 mr-2 flex-shrink-0" />
                        <span className="text-slate-400 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {plan.id === 'free' ? (
                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading === plan.id || (user?.subscription_tier === plan.id)}
                    className="w-full transition-all duration-200 text-base py-6 bg-slate-800 hover:bg-slate-700"
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {loading === plan.id ? 'Chargement...' :
                     user?.subscription_tier === plan.id ? 'Plan actuel' : plan.cta}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Button
                      onClick={() => handleSubscribe(plan.id, '1x')}
                      disabled={loading === plan.id || (user?.subscription_tier === 'club')}
                      className="w-full text-base py-6 bg-gradient-to-r from-[#0D47A1] to-[#00B050] hover:from-[#0A367A] hover:to-[#00853C] hover:shadow-lg transition-all"
                      data-testid="subscribe-club-1x"
                    >
                      {user?.subscription_tier === 'club' ? 'Vous êtes membre' : 'Payer 1 997€ en 1 fois'}
                    </Button>
                    {user?.subscription_tier !== 'club' && (
                      <Button
                        onClick={() => handleSubscribe(plan.id, '4x')}
                        disabled={loading === plan.id}
                        variant="outline"
                        className="w-full text-base py-6 border-2 border-[#0D47A1] text-[#0D47A1] hover:bg-[#0D47A1]/5"
                        data-testid="subscribe-club-4x"
                      >
                        Payer en 4 × 499,25€
                      </Button>
                    )}
                    <p className="text-[11px] text-center text-slate-400 pt-1">
                      Engagement 12 mois · sans reconduction automatique
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Comparez les fonctionnalités
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Fonctionnalité</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Gratuit</th>
                  <th className="text-center py-4 px-4 font-semibold text-[#0D47A1] bg-[#0D47A1]/5">Club 10M</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Calculateur de rentabilité', free: 'Basique', club: 'Complet' },
                  { feature: 'Projets MDB', free: '1', club: 'Illimité' },
                  { feature: 'Dossier bancaire PDF', free: false, club: true },
                  { feature: 'Communauté', free: 'Lecture', club: 'Complet + Badge' },
                  { feature: 'Messages privés', free: false, club: true },
                  { feature: 'Biens immobiliers', free: false, club: true },
                  { feature: 'Formations', free: false, club: true },
                  { feature: 'Annuaire des membres', free: false, club: true },
                  { feature: 'Agent IA', free: false, club: true },
                  { feature: 'Statistiques IA', free: false, club: true },
                  { feature: 'Intégrations (Calendly, Zapier)', free: false, club: true },
                  { feature: 'Alertes email automatiques', free: false, club: true },
                  { feature: 'Support prioritaire', free: false, club: true },
                ].map((row, index) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-4 text-slate-700">{row.feature}</td>
                    <td className="text-center py-4 px-4">
                      {typeof row.free === 'boolean' ? (
                        row.free ? <Check className="h-5 w-5 text-[#00B050] mx-auto" /> : <X className="h-5 w-5 text-slate-300 mx-auto" />
                      ) : (
                        <span className="text-slate-600 text-sm">{row.free}</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-4 bg-[#0D47A1]/5">
                      {typeof row.club === 'boolean' ? (
                        row.club ? <Check className="h-5 w-5 text-[#00B050] mx-auto" /> : <X className="h-5 w-5 text-slate-300 mx-auto" />
                      ) : (
                        <span className="text-slate-600 text-sm">{row.club}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Questions fréquentes
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2">Puis-je annuler mon abonnement ?</h3>
              <p className="text-slate-600">L&apos;abonnement Club 10M Investor est un engagement de 12 mois. Il ne se renouvelle pas automatiquement à la fin de la période : vous décidez librement de rester ou non membre à l&apos;issue des 12 mois.</p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2">Comment fonctionne le paiement en 4 fois ?</h3>
              <p className="text-slate-600">Vous réglez 499,25€ à l&apos;inscription puis 3 prélèvements identiques les mois suivants. Le tarif total reste 1 997€ TTC, sans frais supplémentaires. Vous bénéficiez de l&apos;accès Club immédiatement dès le premier paiement.</p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2">Qu&apos;est-ce que l&apos;agent IA ?</h3>
              <p className="text-slate-600">L&apos;agent IA scrute automatiquement les annonces immobilières et les analyse selon vos critères de rentabilité, cash-flow et zone géographique.</p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2">Les calculs sont-ils fiables ?</h3>
              <p className="text-slate-600">Nos calculateurs sont basés sur la réglementation fiscale française 2025 et ont été validés par des experts-comptables. Ils sont fournis à titre indicatif.</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 items-start">
            <div className="md:col-span-1">
              <div className="mb-6">
                <img src="/logo-footer.png" alt="10 Millions Investor" className="h-24 w-auto" />
              </div>
              <p className="text-sm">
                La plateforme des investisseurs immobiliers ambitieux.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Outils</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/calculator" className="hover:text-white transition-colors">Calculateur</Link></li>
                <li><Link to="/mdb" className="hover:text-white transition-colors">Outil MDB</Link></li>
                <li><Link to="/community" className="hover:text-white transition-colors">Communauté</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Ressources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><Link to="/cgv" className="hover:text-white transition-colors">CGV</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Légal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/charte" className="hover:text-white transition-colors">Charte de Qualité</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Confidentialité</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm flex flex-col sm:flex-row justify-between items-center">
            <span>© 2026 10 Millions Investor. Tous droits réservés.</span>
            <span className="mt-2 sm:mt-0 text-slate-500">
              Création Plateforme <a href="https://webisula.com" target="_blank" rel="noopener noreferrer" className="text-[#00B050] hover:text-white transition-colors font-semibold">Webisula</a>
            </span>
          </div>
        </div>
      </footer>

      {/* Payment Modal - Square on web, Apple IAP on native */}
      {showPaymentForm && !isNativePlatform() && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md">
            <button
              onClick={handlePaymentCancel}
              className="absolute -top-12 right-0 text-white hover:text-slate-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <SquarePaymentForm
              amount={getDisplayAmount()}
              planName={paymentMode === '4x' ? 'Club 10M — 4 × 499,25€' : 'Club 10M Investor'}
              billingPeriod="annual"
              squarePlanIdOverride={getSquarePlanId()}
              isLaunchOffer={false}
              onSuccess={handlePaymentSuccess}
              onCancel={handlePaymentCancel}
              userEmail={user?.email}
              userName={user?.name}
            />
          </div>
        </div>
      )}
      {showPaymentForm && isNativePlatform() && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md">
            <button
              onClick={handlePaymentCancel}
              className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-lg z-10"
              data-testid="close-iap-modal"
            >
              <X className="h-6 w-6 text-slate-600" />
            </button>
            <AppleIAPButton
              onPurchaseSuccess={(tier) => {
                setShowPaymentForm(false);
                handlePaymentSuccess({ tier });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
